// Procedimientos de enfermería paso a paso — referencia rápida.
//
// Formato secuencia: objetivo, materiales, pasos numerados y puntos clave de
// seguridad/asepsia. ⚠️ Referencial/educativo: sigue SIEMPRE el protocolo de tu
// institución y la técnica que te enseñaron; no reemplaza la supervisión clínica.
//
// Dos grupos (plan del mundo TENS, PR7, 2026-10-04):
//  · `tecnicas`: las técnicas de siempre (VVP, sonda vesical, curación).
//  · `cuidados`: los cuidados del día a día del paciente, que es lo que más hace un
//    técnico (cambios de posición, aseo, catéter urinario, caídas, signos vitales,
//    identificación, alimentación y entrega de turno). Son para todos los roles: el
//    TENS los ve primero.
// Los de `cuidados` traen cuatro claves más, todas opcionales para el resto del código:
//  · `porque`: una línea en la voz que enseña (el porqué del cuidado).
//  · `avisa`: de qué le avisas a tu enfermera. Es una lista de lo que se VE, sin umbrales
//    numéricos: qué es "alterado" depende del protocolo de cada unidad.
//  · `preguntas`: tres de repaso por ficha ({ pregunta, opciones, correcta, explica },
//    `correcta` siempre es uno de `opciones` y no va siempre en el mismo lugar: el Reto
//    del día no baraja las opciones). Las lee Modo estudio como el tema "Cuidados"
//    (`preguntasCuidados` en lib/bancoEstudio.js).
//  · `claves`: palabras para que Benito encuentre la ficha (`detectarCuidado` en
//    lib/procedimientosCuidados.js). Van sin tildes, en minúscula, sin siglas de dos letras ni
//    palabras que ya son de otra sección, y sin las que nombran una TÉCNICA ("foley", "sonda
//    vesical"), para que pedir cómo se instala una sonda abra la sección y no el cuidado del
//    catéter.
// Lo que cada ficha respalda y lo que es adaptación de MiPAE vive en la bibliografía
// central (`procedimiento-<id>` en src/data/bibliografia.js), no acá.

export const DISCLAIMER_PROCEDIMIENTOS =
  'Los pasos son una guía para estudiar y repasar. Sigue siempre el protocolo de tu institución y las indicaciones de quien supervisa tu trabajo.';

export const PROCEDIMIENTOS = [
  {
    id: 'vvp',
    grupo: 'tecnicas',
    titulo: 'Vía venosa periférica (VVP)',
    objetivo: 'Instalar un acceso venoso para administrar fluidos o fármacos.',
    materiales: ['Catéter del calibre adecuado', 'Ligadura', 'Antiséptico (clorhexidina/alcohol)', 'Apósito transparente', 'Llave/tapón + suero de prueba', 'Guantes', 'Contenedor de cortopunzantes'],
    pasos: [
      'Higiene de manos y reúne el material.',
      'Identifica al paciente y explícale el procedimiento.',
      'Elige la vena (antebrazo/dorso de mano; evita zonas de flexión).',
      'Coloca la ligadura ~10 cm sobre el sitio.',
      'Antisepsia de la piel y deja secar (no soplar ni abanicar).',
      'Punciona en ángulo ~15–30° y observa el retorno de sangre.',
      'Avanza el catéter, retira la guía y suelta la ligadura.',
      'Conecta, comprueba permeabilidad con suero y fija con apósito.',
      'Rotula (fecha/hora/calibre) y descarta la guía en cortopunzantes.',
    ],
    clave: ['Asepsia + secado del antiséptico = menos flebitis.', 'Nunca reencapuches la guía.', 'Vigila el sitio: dolor, enrojecimiento o edema → retira.'],
  },
  {
    id: 'sonda-vesical',
    grupo: 'tecnicas',
    titulo: 'Sondeo vesical (Foley)',
    objetivo: 'Drenar la vejiga: retención, control estricto de diuresis u otros.',
    materiales: ['Sonda Foley del calibre adecuado', 'Set de cateterismo estéril', 'Guantes estériles', 'Antiséptico', 'Lubricante', 'Jeringa con agua destilada (cuff)', 'Bolsa recolectora'],
    pasos: [
      'Higiene de manos; explica e instala en posición adecuada.',
      'Abre el set con técnica estéril y colócate guantes estériles.',
      'Aseo genital con antiséptico, del meato hacia afuera.',
      'Lubrica la sonda.',
      'Introduce la sonda hasta obtener orina (en hombre, eleva el pene ~90°).',
      'Avanza un poco más e infla el cuff con agua destilada (mL según fabricante).',
      'Tracciona suave para verificar fijación y conecta a la bolsa.',
      'Fija la sonda; deja la bolsa bajo el nivel de la vejiga, sin tocar el suelo.',
    ],
    clave: ['Sistema cerrado: no desconectes la unión sonda-bolsa (riesgo de ITU).', 'Nunca infles el cuff antes de ver orina.', 'Bolsa siempre bajo el nivel vesical para evitar reflujo.'],
  },
  {
    id: 'curacion',
    grupo: 'tecnicas',
    titulo: 'Curación de herida (técnica estéril)',
    objetivo: 'Limpiar y proteger la herida para favorecer la cicatrización.',
    materiales: ['Set de curación estéril', 'Suero fisiológico', 'Gasas estériles', 'Guantes (estériles según herida)', 'Apósito según tipo y exudado', 'Bolsa para desechos'],
    pasos: [
      'Higiene de manos; reúne el material y explica al paciente.',
      'Evalúa la herida (aspecto, exudado, dolor, signos de infección).',
      'Retira el apósito antiguo con guantes de procedimiento y descártalo.',
      'Higiene de manos y técnica estéril para la curación.',
      'Limpia con suero fisiológico de lo más limpio a lo más sucio (centro → periferia).',
      'Seca por arrastre suave con gasa estéril.',
      'Aplica el apósito adecuado al tipo y exudado de la herida.',
      'Registra: aspecto, exudado, apósito usado y próxima curación.',
    ],
    clave: ['De lo limpio a lo sucio, una pasada por gasa.', 'No uses antisépticos citotóxicos sobre tejido de granulación salvo indicación.', 'Documenta la evolución para comparar entre turnos.'],
  },


  // ── Cuidados del paciente ──────────────────────────────────────────────────
  {
    id: 'cambios-posicion',
    grupo: 'cuidados',
    titulo: 'Cambios de posición',
    objetivo: 'Repartir la presión sobre la piel para prevenir las lesiones por presión.',
    materiales: ['Almohadas o cuñas', 'Las ayudas para mover al paciente que use tu unidad', 'Otra persona que ayude, si el paciente pesa mucho o no puede colaborar', 'Registro de la hora y la posición'],
    pasos: [
      'Higiene de manos y explícale al paciente lo que vas a hacer.',
      'Mira en la planificación de tu enfermera qué posición toca, cada cuánto y si hay alguna que no se puede usar.',
      'Revisa las sondas, los drenajes, las vías, el tubo y el circuito del respirador antes de moverlo, para que ninguno se tire ni se doble.',
      'Frena la cama y pide ayuda si la necesitas.',
      'Gíralo sin arrastrarlo, con ayuda de otra persona o con el apoyo que use tu unidad.',
      'Alinea el cuerpo y sostenlo con almohadas o cuñas, sin dejarlo apoyado sobre una zona enrojecida ni sobre un dispositivo.',
      'Mira la piel de la zona que estuvo apoyada, sobre todo el sacro, los talones, las caderas y los hombros.',
      'Deja el timbre al alcance del paciente y pon las barandas según tu protocolo.',
      'Registra la hora y la posición. Si el cambio no se pudo hacer, registra el motivo.',
    ],
    clave: [
      'La frecuencia y la posición las define tu enfermera según el riesgo y la piel de cada paciente. Las guías dan rangos y ninguna cifra sirve para todos. Si no las sabes, pregúntale.',
      'Con un paciente inestable, boca abajo (prono) o con una lesión grave de columna o de cabeza, tu enfermera te dice cómo moverlo. No lo cambies por tu cuenta.',
      'No arrastres al paciente, porque la fricción daña la piel.',
      'Un cambio que no se pudo hacer también se registra, con su motivo.',
    ],
    porque: 'La presión que no se alivia reduce el riego de la piel, y cambiar de posición al paciente es una forma de repartirla para cuidar el tejido.',
    avisa: [
      'Una zona de la piel enrojecida que no recupera su color',
      'Una ampolla, una herida o la piel abierta',
      'Dolor del paciente al moverlo',
      'Un paciente que se descompensó al moverlo, por ejemplo que le falta el aire, está pálido o suda',
      'Una sonda, un drenaje, una vía o un tubo que se tiró o se salió al moverlo',
      'Un cambio que no pudiste hacer, por otra indicación o por el estado del paciente',
    ],
    preguntas: [
      {
        pregunta: '¿Quién define cada cuánto se cambia de posición a un paciente?',
        opciones: ['Cada persona del equipo, según le parezca', 'Siempre cada dos horas, para todos', 'La enfermera, según el riesgo del paciente', 'El paciente, cuando lo pida'],
        correcta: 'La enfermera, según el riesgo del paciente',
        explica: 'La frecuencia es una decisión de enfermería según el riesgo de cada paciente. Quien hace el cambio lo registra con su hora.',
      },
      {
        pregunta: '¿Por qué no se arrastra al paciente cuando se le mueve?',
        opciones: ['Porque se desordena la cama', 'Porque la fricción daña la piel', 'Porque se pierde la hora del registro', 'Porque cansa más a quien lo mueve'],
        correcta: 'Porque la fricción daña la piel',
        explica: 'Por eso se pide ayuda y se usan las ayudas de la unidad, en vez de tirar del cuerpo.',
      },
      {
        pregunta: '¿Qué se mira en la piel al terminar un cambio de posición?',
        opciones: ['Solo la zona que ahora queda apoyada', 'Solo los talones', 'Nada, la piel se revisa solo en el aseo', 'La zona que estuvo apoyada, por si está enrojecida o distinta'],
        correcta: 'La zona que estuvo apoyada, por si está enrojecida o distinta',
        explica: 'Es donde se acumuló la presión. Lo que se ve distinto se registra y se avisa a la enfermera a cargo.',
      },
    ],
    claves: ['cambio de posicion', 'cambios de posicion', 'posicion', 'movilizar', 'movilizacion', 'girar al paciente', 'rotar'],
  },
  {
    id: 'aseo-en-cama',
    grupo: 'cuidados',
    titulo: 'Aseo en cama',
    objetivo: 'Mantener la higiene y el confort de un paciente que no puede levantarse.',
    materiales: ['Agua tibia y jabón neutro', 'Paños o esponja y toalla', 'Ropa personal y de cama limpia', 'Guantes', 'Bolsa para la ropa sucia'],
    pasos: [
      'Higiene de manos, ponte los guantes y explícale al paciente lo que vas a hacer.',
      'Cuida su privacidad cerrando la cortina o la puerta, y descúbrelo solo por partes.',
      'Frena la cama, déjala a una altura cómoda para ti y mira en la planificación si hay posiciones que no se pueden usar.',
      'Lava de lo más limpio a lo más sucio. Parte por la cara y sigue con el cuello, los brazos, el tronco, las piernas y la espalda. La zona genital va al final, con agua limpia y otro paño.',
      'Seca con toques suaves, sin frotar, sobre todo en los pliegues. Mira la piel mientras la secas y lubrícala si la unidad lo indica.',
      'Al terminar la zona genital, cámbiate los guantes y haz higiene de manos antes de tocar la ropa de cama, la cama o el timbre.',
      'Cambia la ropa de cama sin arrastrar al paciente y déjalo cómodo, con la cama frenada y baja, y el timbre a mano.',
      'Retira los guantes, higiene de manos y registra el aseo con su hora.',
    ],
    clave: [
      'Los guantes no reemplazan la higiene de manos.',
      'La humedad que queda entre los pliegues daña la piel.',
      'Aprovecha el aseo para mirar la piel completa.',
    ],
    porque: 'Durante el aseo ves la piel de todo el cuerpo, así que es una buena ocasión para notar cambios, además de dar confort.',
    avisa: [
      'Enrojecimiento, humedad que persiste o piel que se descama',
      'Una herida, una ampolla o un hallazgo nuevo en la piel',
      'Dolor o quejidos del paciente durante el aseo',
      'Una sonda, un drenaje o una vía que se movió o se salió durante el aseo',
    ],
    preguntas: [
      {
        pregunta: '¿En qué orden se lava al paciente en la cama?',
        opciones: ['De lo más limpio a lo más sucio', 'De lo más sucio a lo más limpio', 'Siempre empezando por la espalda', 'Primero la zona genital'],
        correcta: 'De lo más limpio a lo más sucio',
        explica: 'Así no se arrastra la suciedad a zonas limpias, y la zona genital va al final.',
      },
      {
        pregunta: '¿Cómo se seca la piel después del aseo?',
        opciones: ['Frotando fuerte para que quede bien seca', 'Se deja secar sola', 'Con toques suaves, sin frotar, y cuidando los pliegues', 'Solo se secan la cara y las manos'],
        correcta: 'Con toques suaves, sin frotar, y cuidando los pliegues',
        explica: 'Frotar daña la piel, y la humedad que queda en los pliegues también.',
      },
      {
        pregunta: 'Al terminar de lavar la zona genital, y antes de tocar la ropa de cama, ¿qué corresponde?',
        opciones: ['Seguir con los mismos guantes, ya se terminó', 'Cambiar los guantes y hacer higiene de manos', 'Solo secar los guantes con una toalla', 'Esperar a que el paciente se seque'],
        correcta: 'Cambiar los guantes y hacer higiene de manos',
        explica: 'La zona genital es la más contaminada. Con esos guantes se pasaría la suciedad a la cama, a la ropa y al timbre, y los guantes tampoco reemplazan la higiene de manos.',
      },
    ],
    claves: ['aseo', 'aseo en cama', 'aseo completo', 'aseo parcial', 'higiene', 'bano en cama', 'confort', 'ropa de cama'],
  },
  {
    id: 'aseo-bucal',
    grupo: 'cuidados',
    titulo: 'Aseo bucal',
    objetivo: 'Mantener la boca limpia y cómoda, y cuidar sus mucosas.',
    materiales: ['Cepillo suave y pasta de dientes, o esponja y gasas', 'Vaso con agua y riñón o recipiente', 'Linterna', 'El lubricante para labios que use tu unidad', 'Guantes', 'Toalla'],
    pasos: [
      'Higiene de manos, ponte los guantes y explícale al paciente lo que vas a hacer.',
      'Si tiene un tubo en la vía aérea o está sedado, no sigas estos pasos y pregúntale a tu enfermera por el protocolo.',
      'Revisa que esté despierto y pueda escupir y tragar. Si no puede, usa solo gasa o esponja y avisa a tu enfermera.',
      'Incorpóralo, o gíralo de lado si no puede sentarse y su planificación lo permite, para que el agua no se vaya a la garganta.',
      'Si usa prótesis dental, retírala y límpiala aparte.',
      'Mira la boca con la linterna. Revisa los labios, las encías, la lengua y las mejillas.',
      'Limpia dientes, encías y lengua con movimientos suaves. Si puede escupir y tragar, ofrécele un poco de agua para enjuagar. Si no puede, no le des agua y retira el exceso con gasa o esponja.',
      'Lubrica los labios y seca la cara y el contorno de la boca.',
      'Retira los guantes, higiene de manos y registra el aseo con su hora.',
    ],
    clave: [
      'Con un paciente sedado o con un tubo en la vía aérea, sigue el protocolo de tu unidad.',
      'Antes de dar agua, asegúrate de que puede tragar.',
      'Los guantes no reemplazan la higiene de manos.',
      'Mirar la boca es parte del aseo, y lo que ves distinto se avisa.',
    ],
    porque: 'Una boca limpia ayuda a prevenir infecciones y lesiones en las mucosas, y es parte del confort del paciente.',
    avisa: [
      'Sangrado de las encías o de la boca',
      'Heridas, manchas blancas o enrojecimiento nuevo en la boca',
      'Dientes sueltos o rotos',
      'Un paciente que antes podía tragar y ahora no',
      'Tos o atoro durante el aseo',
      'Una prótesis que ya no calza o que lastima',
    ],
    preguntas: [
      {
        pregunta: '¿Cómo se hace el aseo bucal si el paciente no puede tragar con seguridad?',
        opciones: ['Dándole un vaso de agua para que enjuague', 'Ese día no se hace', 'Acostándolo boca arriba para que llegue mejor', 'Limpiando con gasa o esponja y retirando el exceso, sin darle agua'],
        correcta: 'Limpiando con gasa o esponja y retirando el exceso, sin darle agua',
        explica: 'El agua que no se traga bien puede pasar a la vía aérea.',
      },
      {
        pregunta: 'Con un paciente sedado o con un tubo en la vía aérea, ¿qué corresponde en el aseo bucal?',
        opciones: ['Seguir los mismos pasos, con más cuidado', 'Esperar a que le retiren el tubo', 'No seguir estos pasos y consultar el protocolo de la unidad antes de hacerlo', 'Pedirle al familiar que lo haga'],
        correcta: 'No seguir estos pasos y consultar el protocolo de la unidad antes de hacerlo',
        explica: 'Es una técnica con precauciones propias, y cada unidad tiene su protocolo. El técnico lo consulta con la enfermera a cargo. Mirar la boca y avisar lo que se ve distinto sigue siendo parte del aseo.',
      },
      {
        pregunta: 'Si el paciente usa prótesis dental, ¿qué se hace antes de mirar la boca?',
        opciones: ['Dejarla puesta para no molestar', 'Guardarla en una servilleta', 'Retirarla y limpiarla aparte', 'Pedírsela al familiar para que la lave después'],
        correcta: 'Retirarla y limpiarla aparte',
        explica: 'Con la prótesis puesta no se ve la mucosa que queda debajo.',
      },
    ],
    claves: ['aseo bucal', 'boca', 'higiene bucal', 'dientes', 'cepillado', 'protesis dental', 'mucosa oral', 'cuidado de la boca'],
  },
  {
    id: 'cuidado-cateter-urinario',
    grupo: 'cuidados',
    titulo: 'Cuidado del catéter urinario permanente (CUP)',
    objetivo: 'Cuidar el catéter y su sistema de drenaje para prevenir infecciones y lesiones.',
    materiales: ['Guantes', 'Material del aseo diario del paciente', 'Recipiente de medición propio del paciente', 'Toalla'],
    pasos: [
      'Higiene de manos antes y después de tocar el catéter, la bolsa o la zona, y usa guantes.',
      'Mira que el sistema siga cerrado, con la sonda, el tubo y la bolsa unidos y sin desconectar.',
      'Revisa que la bolsa esté siempre más abajo que la vejiga y que no toque el suelo. Cuando muevas al paciente o lo trasladen, mantenla más baja que la vejiga y sin apoyarla en el suelo.',
      'Revisa que el tubo no esté doblado ni aplastado y que la orina pueda fluir.',
      'Revisa que la sonda esté fija y sin tirar del paciente.',
      'Durante el baño o el aseo diario, limpia la zona externa del meato como parte de la higiene de rutina, sin usar antisépticos en esa zona. Si el paciente tiene prepucio, déjalo en su lugar al terminar.',
      'Para vaciar la bolsa usa el recipiente propio del paciente y cuida que no toque el grifo.',
      'Registra el volumen y cómo se ve la orina si tu protocolo lo pide.',
    ],
    clave: [
      'No desconectes el sistema. Si hay que cambiar la bolsa o la sonda, se hace según el protocolo de tu unidad. Si se desconectó, no lo vuelvas a conectar y avisa a tu enfermera.',
      'La bolsa siempre bajo el nivel de la vejiga.',
      'Un recipiente por paciente, nunca el mismo para dos.',
    ],
    porque: 'Un sistema cerrado, con la bolsa abajo y las manos limpias, ayuda a prevenir las infecciones urinarias asociadas al catéter.',
    avisa: [
      'Sangre en la orina, en la bolsa o alrededor de la sonda',
      'Orina con un aspecto distinto al de antes',
      'Casi nada de orina en la bolsa, o un abdomen bajo que se ve o se siente distendido',
      'Orina que se filtra por fuera de la sonda, o una sonda que se movió o se salió',
      'Un sistema desconectado, una bolsa rota o un tubo que no drena',
      'Dolor o molestia del paciente',
    ],
    preguntas: [
      {
        pregunta: '¿Dónde debe estar la bolsa colectora respecto de la vejiga?',
        opciones: ['Más arriba, para que drene mejor', 'Más abajo, sin tocar el suelo', 'A la misma altura', 'En el suelo, para que no se mueva'],
        correcta: 'Más abajo, sin tocar el suelo',
        explica: 'Es una de las medidas que ayudan a prevenir las infecciones urinarias asociadas al catéter.',
      },
      {
        pregunta: 'Si el sistema de drenaje del catéter urinario está desconectado, ¿qué corresponde?',
        opciones: ['No volver a conectarlo y avisar para que se cambie el sistema', 'Conectarlo rápido con las manos limpias', 'Limpiar la unión con alcohol y conectarlo', 'Esperar a vaciar la bolsa para ver qué pasa'],
        correcta: 'No volver a conectarlo y avisar para que se cambie el sistema',
        explica: 'Un sistema desconectado deja de ser cerrado. Se cambia con técnica aséptica según el protocolo de la unidad, y el técnico lo avisa a la enfermera a cargo.',
      },
      {
        pregunta: 'Para vaciar la bolsa, ¿qué recipiente se usa?',
        opciones: ['El de la pieza de al lado, para ahorrar tiempo', 'Cualquiera limpio', 'El mismo para todos, lavado al final del turno', 'El propio de ese paciente, sin tocar el grifo'],
        correcta: 'El propio de ese paciente, sin tocar el grifo',
        explica: 'Un recipiente por paciente evita pasar bacterias entre pacientes.',
      },
    ],
    claves: ['cup', 'cateter urinario', 'bolsa colectora', 'itu', 'itu asociada a cateter', 'vaciar bolsa'],
  },
  {
    id: 'prevencion-caidas',
    grupo: 'cuidados',
    titulo: 'Prevención de caídas',
    objetivo: 'Aplicar las medidas de prevención de caídas que define tu enfermera y registrar las que aplicas.',
    materiales: ['La planificación de cuidados de tu enfermera', 'Barandas y frenos de la cama', 'Calzado del paciente', 'Registro de las medidas aplicadas'],
    pasos: [
      'Mira en la planificación qué medidas indicó tu enfermera para este paciente.',
      'Con el paciente recostado, deja las barandas de la cama en alto.',
      'Deja la cama frenada y baja, y el timbre y las cosas del paciente al alcance.',
      'Supervisa el uso correcto del calzado y del vestuario, y de las sandalias o pantuflas si las usa.',
      'Si se va a levantar, siéntalo un momento al borde de la cama antes de pararlo. Supervisa o asiste cuando se levanta, camina o va al baño, según lo indicado.',
      'En la ducha, ayúdalo y supervísalo. Usa materiales antideslizantes, procura que use los pasamanos y sécalo bien para que no resbale.',
      'Cuéntale al paciente y a su familia, con palabras simples, por qué las medidas importan.',
      'Si el paciente se cae, quédate con él, pide ayuda y avisa a tu enfermera. No lo levantes ni lo muevas hasta que ella lo vea.',
      'Registra con su hora las medidas que aplicaste.',
    ],
    clave: [
      'Tu enfermera define las medidas. Tú las aplicas y las registras.',
      'Si un paciente se cae, quédate con él, pide ayuda y avisa a tu enfermera. No lo levantes ni lo muevas hasta que ella lo vea.',
      'Evitar todas las caídas es difícil, por eso importa aplicar cada medida indicada y explicarle al paciente y a su familia por qué se hace.',
    ],
    porque: 'Una caída en el hospital puede lesionar a una persona que ya estaba enferma. Casi todas las medidas son gestos simples que se hacen en cada turno.',
    avisa: [
      'Una caída, aunque el paciente parezca estar bien',
      'Un paciente que se levanta solo, aunque tenga indicado hacerlo con ayuda',
      'Un paciente que intenta saltar o trepar las barandas',
      'Mareo, confusión o un cambio en cómo está el paciente',
      'Una medida que no puedes aplicar',
    ],
    preguntas: [
      {
        pregunta: '¿Quién decide qué medidas de prevención de caídas lleva cada paciente?',
        opciones: ['Cada persona del equipo, según le parezca', 'El propio paciente', 'La enfermera, según el riesgo del paciente', 'Es igual para todos, no se decide'],
        correcta: 'La enfermera, según el riesgo del paciente',
        explica: 'Quien atiende al paciente aplica las medidas indicadas y las registra.',
      },
      {
        pregunta: '¿Cuál de estas medidas corresponde cuando el paciente está recostado?',
        opciones: ['Dejar el timbre fuera de su alcance', 'Quitarle el calzado y dejarlo descalzo', 'Barandas de la cama en alto', 'Dejar la cama en la posición más alta'],
        correcta: 'Barandas de la cama en alto',
        explica: 'Es una de las medidas básicas de prevención de caídas.',
      },
      {
        pregunta: '¿Qué corresponde si un paciente se cae?',
        opciones: ['Levantarlo entre dos y después avisar', 'Quedarse con él, pedir ayuda y avisar, sin levantarlo', 'Esperar al final del turno para contarlo', 'Contárselo solo al familiar'],
        correcta: 'Quedarse con él, pedir ayuda y avisar, sin levantarlo',
        explica: 'Mientras no lo evalúe la enfermera no se sabe si hay una lesión, así que no se mueve. Una caída se avisa siempre, aunque el paciente parezca estar bien.',
      },
    ],
    claves: ['caida', 'caidas', 'se cae', 'se cayo', 'riesgo de caidas', 'barandas', 'levantada', 'deambulacion', 'prevencion de caidas', 'calzado'],
  },
  {
    id: 'signos-vitales',
    grupo: 'cuidados',
    titulo: 'Control de signos vitales',
    objetivo: 'Medir los signos vitales y dejarlos registrados con su hora.',
    materiales: ['Monitor o tensiómetro, termómetro y oxímetro', 'Reloj con segundero', 'Registro de signos vitales'],
    pasos: [
      'Higiene de manos y verifica que los equipos funcionan.',
      'Identifica al paciente con dos datos y explícale lo que vas a hacer.',
      'Déjalo cómodo y tranquilo antes de medir.',
      'Mide presión arterial, pulso, frecuencia respiratoria, temperatura y saturación con la técnica que te enseñaron. No tomes la presión en un brazo con fístula o con una vía venosa.',
      'Si el paciente se ve mal, avisa de inmediato, sin esperar a medir de nuevo.',
      'Si el paciente se ve bien y un valor no cuadra con cómo está, vuelve a medir.',
      'Registra cada valor tal como lo midiste, con su hora.',
      'Si un valor te preocupa o tu unidad pide avisarlo, avisa a tu enfermera.',
      'Higiene de manos y deja los equipos limpios.',
    ],
    clave: [
      'Qué valor se considera alterado lo define el protocolo de tu unidad y el estado de cada paciente.',
      'Registra el valor tal como lo mediste, aunque te parezca raro. Si dudas de la medición, vuelve a medir.',
      'Si un valor te preocupa, avisa a tu enfermera, aunque no tengas claro si pasa el límite de tu protocolo.',
    ],
    porque: 'Los signos vitales ayudan a notar que un paciente está cambiando, y por eso importa medirlos con cuidado y dejarlos registrados.',
    avisa: [
      'Una alteración importante de los signos vitales, según el protocolo de tu unidad',
      'Dificultad para respirar, de inmediato',
      'Piel pálida, fría o sudorosa, o labios azulados, de inmediato',
      'Confusión, o un paciente muy difícil de despertar, de inmediato',
      'Una convulsión, un desmayo o un sangrado abundante, de inmediato',
      'Un cambio en cómo se ve o se comporta el paciente, aunque los números parezcan normales',
    ],
    preguntas: [
      {
        pregunta: 'Si un signo vital preocupa, ¿qué corresponde?',
        opciones: ['Esperar a la próxima toma para ver si cambia', 'Anotarlo y seguir con la ronda', 'Avisar a quien corresponde', 'Comentárselo solo al paciente'],
        correcta: 'Avisar a quien corresponde',
        explica: 'Lo que preocupa no se guarda para la próxima toma. El técnico avisa a la enfermera a cargo, y la enfermera decide qué hacer.',
      },
      {
        pregunta: '¿De qué depende lo que se considera un valor alterado?',
        opciones: ['Del protocolo de la unidad y del paciente', 'De una cifra que es igual en todo Chile', 'De lo que diga el monitor', 'De lo que pida el familiar'],
        correcta: 'Del protocolo de la unidad y del paciente',
        explica: 'Cada unidad define sus límites, así que aquí no hay cifras. Se registra lo medido, con su hora.',
      },
      {
        pregunta: 'Un paciente se ve bien y uno de sus signos vitales no cuadra con cómo está. ¿Qué se hace primero?',
        opciones: ['Registrar el valor y seguir con el siguiente paciente', 'Pedir que otra persona lo mida', 'Esperar una hora para avisar', 'Volver a medir'],
        correcta: 'Volver a medir',
        explica: 'Un equipo mal puesto o un paciente inquieto cambian la medición. Si sigue sin cuadrar, o si el paciente se ve mal, se avisa sin esperar.',
      },
    ],
    claves: ['signos vitales', 'presion arterial', 'tomar la presion', 'tomo la presion', 'pulso', 'frecuencia cardiaca', 'frecuencia respiratoria', 'temperatura', 'saturacion', 'spo2', 'toma de signos', 'control de signos'],
  },
  {
    id: 'identificacion-paciente',
    grupo: 'cuidados',
    titulo: 'Identificación del paciente',
    objetivo: 'Confirmar que el paciente es el correcto antes de cada cuidado o procedimiento.',
    materiales: ['Brazalete de identificación del paciente', 'La ficha o la planificación del paciente'],
    pasos: [
      'Higiene de manos y preséntate al paciente.',
      'Pregúntale su nombre completo y un segundo dato que use tu institución, como su fecha de nacimiento o su RUT.',
      'Compara lo que responde con el brazalete y con la ficha.',
      'No uses el número de cama ni de pieza para identificarlo, porque puede cambiar.',
      'Si no puede responder, verifica con el brazalete y la ficha, o con su familiar, como indique tu protocolo.',
      'Si los datos no coinciden o no puedes confirmar quién es, no sigas con el procedimiento y avisa a tu enfermera.',
      'Repite la verificación antes de cada procedimiento, de cada muestra o de cada traslado.',
      'Si no trae brazalete o está ilegible, avisa a tu enfermera y pide que lo repongan.',
    ],
    clave: [
      'Siempre dos datos, y ninguno es el número de cama.',
      'Se verifica cada vez, no solo al ingreso.',
      'Si dos pacientes tienen nombres parecidos, se confirma con más cuidado.',
      'Un brazalete ausente o ilegible se avisa y se repone.',
    ],
    porque: 'Un error grave puede empezar cuando se hace algo correcto a la persona equivocada. Confirmar dos datos ayuda a evitarlo.',
    avisa: [
      'Un brazalete ausente, ilegible o deteriorado',
      'Datos que no coinciden entre el brazalete y la ficha',
      'Un paciente que no responde a su nombre y no hay forma de confirmar quién es',
    ],
    preguntas: [
      {
        pregunta: '¿Cuál de estos NO sirve para identificar a un paciente?',
        opciones: ['El nombre completo', 'La fecha de nacimiento', 'El número de cama', 'El RUT'],
        correcta: 'El número de cama',
        explica: 'La cama o la pieza pueden cambiar, y la persona no.',
      },
      {
        pregunta: '¿Cuándo se verifica la identidad del paciente?',
        opciones: ['Solo al ingreso', 'Antes de cada procedimiento, muestra o traslado', 'Solo cuando hay dudas', 'Una vez por turno'],
        correcta: 'Antes de cada procedimiento, muestra o traslado',
        explica: 'Se repite cada vez, porque el error puede ocurrir en cualquier cuidado.',
      },
      {
        pregunta: 'Si el paciente no trae brazalete, ¿qué corresponde?',
        opciones: ['Seguir igual, ya se le conoce', 'Esperar a que el familiar confirme quién es y seguir', 'Esperar a que el paciente se acuerde', 'Avisar y pedir que lo repongan'],
        correcta: 'Avisar y pedir que lo repongan',
        explica: 'Sin brazalete no hay forma segura de confirmar quién es. El técnico lo avisa a la enfermera a cargo, y la reposición sigue el protocolo de la unidad.',
      },
    ],
    claves: ['identificacion', 'identificar paciente', 'brazalete', 'pulsera', 'dos identificadores', 'verificar identidad', 'paciente correcto'],
  },
  {
    id: 'alimentacion-asistida',
    grupo: 'cuidados',
    titulo: 'Alimentación asistida',
    objetivo: 'Ayudar a comer a un paciente que no puede hacerlo solo, de forma segura.',
    materiales: ['Bandeja con la dieta indicada', 'Servilleta o babero', 'Cuchara o cubiertos adaptados', 'Agua u otro líquido indicado'],
    pasos: [
      'Higiene de manos, la tuya y la del paciente.',
      'Confirma en la planificación el régimen, la consistencia y si tiene alergias alimentarias, y verifica con dos datos que la bandeja es de este paciente.',
      'Revisa que esté despierto y en condiciones de comer. Si está muy somnoliento o no puede tragar, no le des de comer y avisa.',
      'Siéntalo o incorpóralo lo más que permita su condición.',
      'Ofrécele pequeñas cantidades y espera a que trague antes de darle más.',
      'Observa si tose, se atora o la voz le suena húmeda al comer. Si tose o se atora, detente y avisa.',
      'Si no puede respirar o hablar, pide ayuda de inmediato.',
      'Después de comer, mantén la posición incorporada según la indicación y haz el aseo bucal.',
      'Registra cuánto comió y cómo lo toleró, como indique tu protocolo.',
    ],
    clave: [
      'La dieta y la consistencia las indica tu equipo, y se confirman antes de dar de comer.',
      'Si tose o se atora, detente y avisa. Si no puede respirar o hablar, pide ayuda de inmediato.',
      'Aunque no tosa, puede haber aspirado. Si lo ves distinto después de comer, avisa.',
      'Con la comida no se apura. Se dan pequeñas cantidades y a su ritmo.',
    ],
    porque: 'Comer es una necesidad básica, pero si el alimento pasa a la vía aérea puede ser grave. Por eso se hace sentado y con calma.',
    avisa: [
      'Tos, atoro o voz húmeda al comer',
      'Dificultad para tragar',
      'Rechazo de la comida o vómitos',
      'Una bandeja que no corresponde a la dieta indicada',
      'Una reacción distinta después de comer, como picazón o hinchazón',
      'Hinchazón de labios o lengua, o dificultad para respirar después de comer, de inmediato',
    ],
    preguntas: [
      {
        pregunta: 'Antes de dar de comer, ¿qué se confirma?',
        opciones: ['Solo que la comida esté caliente', 'Que le guste lo que hay', 'El régimen indicado, las alergias y que la bandeja es de ese paciente', 'Nada, la bandeja siempre está bien'],
        correcta: 'El régimen indicado, las alergias y que la bandeja es de ese paciente',
        explica: 'La dieta y la consistencia las indica el equipo, y un error puede ser grave.',
      },
      {
        pregunta: '¿En qué posición se alimenta al paciente?',
        opciones: ['Sentado o lo más incorporado que permita su condición', 'Acostado boca arriba', 'Como lo pida el paciente, sin importar su condición', 'Como le acomode a quien lo alimenta'],
        correcta: 'Sentado o lo más incorporado que permita su condición',
        explica: 'Así disminuye el riesgo de que el alimento pase a la vía aérea.',
      },
      {
        pregunta: 'Si el paciente tose o se atora al comer, ¿qué corresponde?',
        opciones: ['Seguir dándole despacio', 'Darle agua rápido', 'Esperar al final de la comida para contarlo', 'Detenerse y avisar'],
        correcta: 'Detenerse y avisar',
        explica: 'Tos o atoro pueden ser una señal de que está aspirando, y el técnico lo avisa a la enfermera a cargo. Si no puede respirar o hablar, se pide ayuda de inmediato.',
      },
    ],
    claves: ['alimentacion', 'alimentacion asistida', 'comida', 'dar de comer', 'doy de comer', 'ayudar a comer', 'asistir alimentacion', 'aspiracion', 'dieta', 'bandeja', 'tragar', 'disfagia'],
  },
  {
    id: 'entrega-turno-tens',
    grupo: 'cuidados',
    titulo: 'Entrega de turno del técnico',
    objetivo: 'Entregar al técnico que viene lo que pasó en tu turno y lo que queda para el suyo.',
    materiales: ['Tus registros del turno (en MiPAE, la Entrega del turno)', 'El libro de entrega de tu unidad'],
    pasos: [
      'Antes de la hora de entrega, revisa tus registros del turno de cada paciente.',
      'Haz higiene de manos. Haz la entrega frente al paciente, con el técnico que recibe, salvo que tu protocolo indique otra cosa, como con un paciente en aislamiento.',
      'Cuida la privacidad. Si hay visitas o acompañantes, pídeles que salgan un momento, y en una pieza compartida cuida lo que se escucha.',
      'Empieza por las novedades y los eventos importantes del turno.',
      'Cuéntale lo que va para el turno siguiente, con su motivo, y lo que está por informar a la enfermera.',
      'Revisa con el técnico que recibe los cuidados realizados, los dispositivos y el estado de la piel.',
      'Deja escrito en el libro de entrega lo que corresponda a tu unidad.',
    ],
    clave: [
      'La entrega se hace frente al paciente y cuidando su privacidad, salvo que tu protocolo diga otra cosa, como con un paciente en aislamiento.',
      'Lo que queda para el turno siguiente se entrega con su motivo.',
      'La entrega parte de lo que registraste en tu turno, con su hora.',
    ],
    porque: 'Una entrega clara ayuda a que el paciente no quede sin un cuidado entre un turno y otro.',
    avisa: [
      'Algo importante del turno que quedó por informar',
      'Un evento importante del turno que todavía no alcanzaste a registrar',
    ],
    preguntas: [
      {
        pregunta: 'Si la entrega se hace frente al paciente y hay visitas en la pieza, ¿qué corresponde?',
        opciones: ['Entregar igual, con las visitas escuchando', 'Pedir a las visitas que salgan un momento, para cuidar la privacidad', 'Dejar la entrega para después', 'Entregar solo por escrito'],
        correcta: 'Pedir a las visitas que salgan un momento, para cuidar la privacidad',
        explica: 'Con visitas en la pieza se escucha todo lo del paciente, y pedirles que salgan un momento cuida su privacidad. Si la unidad lo hace distinto, manda su protocolo.',
      },
      {
        pregunta: '¿Qué se hace con lo que queda para el turno siguiente?',
        opciones: ['Se omite para no alargar la entrega', 'Se anota en un papel suelto', 'Lo resuelve el turno siguiente por su cuenta', 'Se entrega con su motivo'],
        correcta: 'Se entrega con su motivo',
        explica: 'Lo que queda pendiente se entrega con su motivo, para que quien recibe sepa por qué no se hizo y qué sigue.',
      },
      {
        pregunta: 'En la entrega de turno del técnico, ¿qué se revisa con quien recibe?',
        opciones: ['Solo la ropa de cama', 'Solo las visitas del paciente', 'Los cuidados realizados, los dispositivos y la piel', 'Solo los horarios de la próxima comida'],
        correcta: 'Los cuidados realizados, los dispositivos y la piel',
        explica: 'Así quien recibe queda al tanto de lo que ya se hizo y de lo que hay que vigilar. Cada unidad puede agregar otros puntos.',
      },
    ],
    claves: ['entrega de turno', 'recepcion de turno', 'libro de entrega', 'cambio de turno', 'relevo de turno'],
  },
];
